"use client";

import { FC, PropsWithChildren, ReactNode } from "react";
import { Card, CVECard } from "@/types/General";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { Ordering, Theme } from "@/types";
import { Button } from "../Button";
import { getDataLinkAttributes } from "@/helpers";
import { twMerge } from "@/helpers/twMerge";

export type ColumnConfig = {
  titleKey: string;
  width: number;
  sortField?: SortField;
  cellClassName?: string;
  renderCell: (card: Card & CVECard) => ReactNode;
};

const TableCell: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => (
  <td
    className={twMerge("text-link-s text-swdc-gray-400 px-3 py-4", className)}
  >
    {children}
  </td>
);

type SortDirection = "ascending" | "descending" | null;
type SortField =
  "date" | "updatedDate" | "fixedVersion" | "severity" | "isFeaturedAndDate";

type SortableIndexTableProps = {
  cards: (Card & CVECard)[];
  columns: ColumnConfig[];
  columnTitles: Record<string, string>;
  showMore: {
    text: string;
    dlt?: string;
    dld?: string;
  };
  hasMore: boolean;
  isLoading?: boolean;
  currentSortField?: SortField;
  currentSortDirection?: Ordering["order"];
  onShowMore: () => void;
  onSort?: (field: SortField, order: Ordering["order"] | null) => void;
  theme?: Theme;
};

export const SortableIndexTable: FC<SortableIndexTableProps> = ({
  cards,
  columns,
  columnTitles,
  showMore,
  hasMore,
  isLoading,
  currentSortField,
  currentSortDirection,
  onShowMore,
  onSort,
}) => {
  const minTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const getSortDirection = (field: SortField): SortDirection => {
    if (currentSortField !== field) return null;
    return currentSortDirection ?? null;
  };

  const handleSort = (field: SortField) => (direction: SortDirection) => {
    if (!onSort) return;
    onSort(field, direction);
  };

  const renderColumn = (col: ColumnConfig) => {
    const isSortable = !!col.sortField;
    return (
      <SortableColumnHeader
        key={col.titleKey}
        label={columnTitles[col.titleKey] ?? col.titleKey}
        isSortable={isSortable}
        sortDirection={
          col.sortField ? getSortDirection(col.sortField) : undefined
        }
        onSort={col.sortField ? handleSort(col.sortField) : undefined}
      />
    );
  };

  return (
    <div className="w-full">
      <div className="overflow-x-auto pb-10">
        <table
          className="w-full table-fixed border-collapse"
          style={{ minWidth: `${minTableWidth}px` }}
        >
          <colgroup>
            {columns.map((col) => (
              <col key={col.titleKey} style={{ width: `${col.width}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-swdc-silver-300 border-b-2">
              {columns.map(renderColumn)}
            </tr>
          </thead>
          <tbody>
            {cards.map((card) => {
              return (
                <tr
                  key={`${card.guid}-${card.index}-${card.link?.url}`}
                  className="group border-swdc-silver-300 focus-within:outline-swdc-blue-400 hover:bg-swdc-cool-gray-200 relative cursor-pointer border-b -outline-offset-2 transition-colors focus-within:outline-2"
                >
                  {columns.map((col) => (
                    <TableCell
                      key={`${col.titleKey}-${card.guid}-${card.link?.url}`}
                      className={col.cellClassName}
                    >
                      {col.renderCell(card)}
                    </TableCell>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Show More button */}
      {hasMore && (
        <div className="mt-10 flex justify-center">
          <Button
            variant="tertiary"
            size="m"
            icon="arrowDown"
            iconPosition="right"
            {...getDataLinkAttributes(showMore)}
            onClick={onShowMore}
            disabled={isLoading}
          >
            {showMore.text}
          </Button>
        </div>
      )}
    </div>
  );
};
